import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Square, DoorOpen, DoorClosed, MoveHorizontal } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useTranslation } from '../context/TranslationContext';

const ConfiguratorSelection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const configuratorTypes = [
    {
      id: 'windows',
      nameKey: 'configurator.kozijnen',
      descriptionKey: 'configurator.kozijnenDesc',
      icon: Square,
      color: 'bg-blue-100',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      path: '/kozijnen' // Maps to Configurator (windows)
    },
    {
      id: 'patio-door',
      nameKey: 'configurator.terrasdeur',
      descriptionKey: 'configurator.terrasdeurDesc',
      icon: DoorOpen,
      color: 'bg-green-100',
      iconColor: 'text-green-600',
      borderColor: 'border-green-500',
      path: '/terrasdeur'
    },
    {
      id: 'back-door',
      nameKey: 'configurator.achterdeur',
      descriptionKey: 'configurator.achterdeurDesc',
      icon: DoorClosed,
      color: 'bg-purple-100',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-500',
      path: '/achterdeur'
    },
    {
      id: 'front-door',
      nameKey: 'configurator.voordeur',
      descriptionKey: 'configurator.voordeurDesc',
      icon: DoorClosed,
      color: 'bg-orange-100',
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-500',
      path: '/voordeur'
    },
    {
      id: 'sliding-door',
      nameKey: 'configurator.schuifpui',
      descriptionKey: 'configurator.schuifpuiDesc',
      icon: MoveHorizontal,
      color: 'bg-teal-100',
      iconColor: 'text-teal-600',
      borderColor: 'border-teal-500',
      path: '/schuifpui'
    },
    {
      id: 'keralit',
      nameKey: 'configurator.keralit',
      descriptionKey: 'configurator.keralitDesc',
      icon: Square,
      color: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      borderColor: 'border-indigo-500',
      path: '/keralit'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{t('configurator.backToHome')}</span>
            </button>
            <Badge className="bg-orange-100 text-orange-800 border-orange-200">
              {t('configurator.step1of2')}
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('configurator.selectType')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('configurator.selectTypeDescription')}
          </p>
        </div>

        {/* Configurator Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {configuratorTypes.map((type) => {
            const Icon = type.icon;
            return (
              <Card 
                key={type.id}
                className="group cursor-pointer border-2 hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={() => navigate(type.path)}
              >
                <CardHeader className="text-center pb-4">
                  <div className={`w-20 h-20 ${type.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-10 h-10 ${type.iconColor}`} />
                  </div>
                  <CardTitle className="text-2xl mb-2">{t(type.nameKey)}</CardTitle>
                  <CardDescription className="text-base">
                    {t(type.descriptionKey)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    className={`w-full bg-slate-800 hover:bg-slate-900 group-hover:bg-orange-600 transition-colors`}
                    size="lg"
                  >
                    {t('configurator.configureNow')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-xl p-8 border-2 border-gray-200 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            {t('configurator.notSure')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('configurator.helpDescription')}
          </p>
          <Button variant="outline" size="lg" className="border-2">
            {t('configurator.contactSupport')}
          </Button>
        </div>
      </div>

      {/* Features Banner */}
      <div className="bg-slate-800 py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">5</div>
              <div className="text-slate-300">{t('configurator.productTypes')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">3 Min</div>
              <div className="text-slate-300">{t('configurator.averageConfiguration')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">100%</div>
              <div className="text-slate-300">{t('configurator.accuracyGuaranteed')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguratorSelection;

